import React, { useEffect, useRef, useState } from 'react'
import {
	Button,
	Popconfirm,
	Switch,
	Table,
	TablePaginationConfig,
	Tooltip,
} from 'antd'
import { ClientRole, ClientUser, MobileNumber } from 'types'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { clearUsers, getAllClientUsersAction } from 'slices/users'
import getNewClientUserPassword from 'api/users/getNewClientUserPassword'
import copyToClipboard from 'utils/copyToClipboard'
import handleAxiosError from 'utils/handleAxiosError'

import { RiLockPasswordLine } from 'react-icons/ri'

/**
 * Hardcoded size of the table
 */
const size = 10

interface PropTypes {
	query: string
	clientId: string
}

const UsersTable: React.FC<PropTypes> = ({ query, clientId }) => {
	const dispatch = useAppDispatch()
	const {
		records: userRecords,
		currentPage,
		totalCount,
		loading,
		error: loadError,
	} = useAppSelector(state => state.users)
	const prevClientIdRef = useRef(clientId)

	const [generatingPasswordId, setGeneratingPasswordId] = useState<string>('')

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize: size,
	}

	useEffect(() => {
		if (prevClientIdRef.current !== clientId) {
			dispatch(clearUsers())
			prevClientIdRef.current = clientId
		}
		const options = {
			page: 1,
			size: pagination.pageSize,
			client: clientId,
			query,
		}
		dispatch(getAllClientUsersAction(options))
	}, [clientId, query, dispatch])

	/**
	 * Handles pagination change by changing the current page in the store.
	 *
	 * @param page - Pagination object from the table
	 */

	const handlePageChange = (page: TablePaginationConfig) => {
		const options = {
			page: page.current || 1,
			size: page.pageSize || size,
			client: clientId,
			query,
		}
		dispatch(getAllClientUsersAction(options))
	}

	const handlePasswordReset = async (record: ClientUser) => {
		try {
			setGeneratingPasswordId(record._id)

			const newPasword = await getNewClientUserPassword(record._id)

			copyToClipboard(
				`Email: ${record.email} Password: ${newPasword}`,
				'Email & Password copied to clipboard'
			)
		} catch (error: any) {
			handleAxiosError(error)
		} finally {
			setGeneratingPasswordId('')
		}
	}

	/**
	 * Columns for the table of Invoice
	 */

	const columns = [
		{
			title: 'Name',
			dataIndex: 'firstName',
			render: (firstName: string, data: ClientUser) =>
				`${firstName} ${data.lastName && data.lastName}`,
		},
		{
			title: 'Role',
			dataIndex: 'role',
			render: (role: ClientRole) => role.role,
		},
		{
			title: 'Mobile Number',
			dataIndex: 'mobileNumber',
			render: (mobileNumber: MobileNumber) =>
				`+${mobileNumber.code} ${mobileNumber.mobile}`,
		},
		{
			title: 'Email',
			dataIndex: 'email',
		},
		{
			title: 'Status',
			dataIndex: 'active',
			render: (active: boolean) => <Switch disabled defaultChecked={active} />,
		},
		{
			title: 'Actions',
			dataIndex: '_id',
			render: (id: string, record: ClientUser) => (
				<Popconfirm
					title='Are you sure?'
					onConfirm={() => handlePasswordReset(record)}
				>
					<Tooltip title='Generate password'>
						<Button
							loading={generatingPasswordId === record._id}
							icon={<RiLockPasswordLine />}
						/>
					</Tooltip>
				</Popconfirm>
			),
		},
	]

	return (
		<Table
			columns={columns}
			rowKey={record => record._id}
			dataSource={userRecords}
			pagination={{ ...pagination, position: ['bottomCenter'] }}
			loading={loading}
			onChange={handlePageChange}
			scroll={{ x: 'fit-content' }}
			locale={{
				emptyText: loadError && !loading ? loadError : 'No Data',
			}}
		/>
	)
}

export default UsersTable
